import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, Users, Target, DollarSign, CheckCircle,
  Loader2, ChevronLeft, ChevronRight, Save, Check, Sparkles,
  Wand2, BookOpen, ChevronDown, ChevronUp, Copy,
} from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';
import { isClientReadyForRecruitment } from '@core/clients/clientEngagementGate';
import { clientService } from '@core/supabase/services/clientService';
import { requisitionService } from '@core/supabase/services/requisitionService';
import { supabase } from '@core/supabase/client';
import { invokeLlamaChat, parseSkillLines } from '@core/ai/llamaChat';
import { REQUISITION_TEMPLATES } from './requisitionTemplates';
import type { Client, Requisition, RequisitionInsert } from '@core/types';
import { REQUISITION_PRIORITIES, type RequisitionPriority } from '@core/enums';

const AI_SYSTEM = `You are an expert recruiting operations assistant. Be concise and practical.
When listing skills, output one skill per line with no numbering.
Always output plain text — no markdown formatting, no asterisks, no hash headers, no bullet dashes.`;

/** Convert AI markdown to clean plain text */
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')   // **bold**
    .replace(/\*(.+?)\*/g, '$1')        // *italic*
    .replace(/^#+\s+/gm, '')            // ## headers
    .replace(/^[-*]\s+/gm, '• ')        // - bullet / * bullet → • bullet
    .replace(/^\d+\.\s+/gm, (m) => m)   // keep numbered list as-is
    .replace(/`(.+?)`/g, '$1')          // `code`
    .trim();
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  low:    { label: 'Low',    color: '#6b7280', bg: '#6b728015', border: '#6b728040' },
  normal: { label: 'Normal', color: '#10b981', bg: '#10b98115', border: '#10b98140' },
  high:   { label: 'High',   color: '#f59e0b', bg: '#f59e0b15', border: '#f59e0b40' },
  urgent: { label: 'Urgent', color: '#ef4444', bg: '#ef444415', border: '#ef444440' },
};

/** Quick AI prompts per step — autoApply means result goes directly to form field */
const STEP_PROMPTS: Record<number, { label: string; prompt: string; apply: 'must' | 'nice' | 'justification' | 'notes'; autoApply: boolean }[]> = {
  3: [
    { label: '✦ Suggest must-have skills',    apply: 'must',          autoApply: true,  prompt: 'List 8–10 must-have skills for this role. One per line, no intro, no bullets.' },
    { label: '✦ Suggest nice-to-have skills', apply: 'nice',          autoApply: true,  prompt: 'List 6–8 nice-to-have skills for this role. One per line, no intro, no bullets.' },
    { label: '✦ Draft justification',          apply: 'justification', autoApply: true,  prompt: 'Write a 3-paragraph business justification: business need, scope of role, success criteria. Professional tone.' },
  ],
  5: [
    { label: '✦ Approval summary',            apply: 'notes',         autoApply: true,  prompt: 'Write a 5-bullet executive summary of this requisition for internal approval. Start each bullet with a bold label.' },
  ],
};

/** Unified contact shape for autofill suggestions */
interface ManagerSuggestion {
  key: string;
  full_name: string;
  email: string | null;
  source: 'org_user' | 'past_req';
}

const BRAND = '#8B80F9';
const TOTAL = 5;

const STEPS = [
  { n: 1, icon: ClipboardList, title: 'Client & role',   subtitle: 'Who is hiring and what is the role?' },
  { n: 2, icon: Users,         title: 'People & location', subtitle: 'Hiring manager and work arrangement.' },
  { n: 3, icon: Target,        title: 'Role scope',      subtitle: 'Justification and required skills.' },
  { n: 4, icon: DollarSign,    title: 'Compensation',    subtitle: 'Budget expectations and range.' },
  { n: 5, icon: CheckCircle,   title: 'Review & create', subtitle: 'Confirm and finish intake.' },
] as const;

type WizardForm = {
  client_id: string;
  title: string;
  department: string;
  headcount: string;
  priority: RequisitionPriority;
  hiring_manager_name: string;
  hiring_manager_email: string;
  alt_spoc_name: string;
  alt_spoc_email: string;
  location: string;
  employment_type: string;
  target_start_date: string;
  business_justification: string;
  must_have_skills: string[];
  nice_to_have_skills: string[];
  budget_currency: string;
  budget_min: string;
  budget_max: string;
  internal_notes: string;
};

function emptyForm(): WizardForm {
  return {
    client_id: '', title: '', department: '', headcount: '1', priority: 'normal',
    hiring_manager_name: '', hiring_manager_email: '',
    alt_spoc_name: '', alt_spoc_email: '',
    location: '', employment_type: '', target_start_date: '',
    business_justification: '', must_have_skills: [], nice_to_have_skills: [],
    budget_currency: 'INR', budget_min: '', budget_max: '', internal_notes: '',
  };
}

function toBaseFields(f: WizardForm) {
  /* Prepend alt SPOC to internal_notes if filled */
  const spocLine = f.alt_spoc_name.trim()
    ? `Alt. SPOC: ${f.alt_spoc_name.trim()}${f.alt_spoc_email.trim() ? ` <${f.alt_spoc_email.trim()}>` : ''}`
    : '';
  const notesBody = f.internal_notes.trim();
  const mergedNotes = spocLine
    ? (notesBody ? `${spocLine}\n\n${notesBody}` : spocLine)
    : (notesBody || null);

  return {
    title: f.title.trim(),
    department: f.department.trim() || null,
    headcount: Math.max(1, parseInt(f.headcount, 10) || 1),
    priority: f.priority,
    status: 'draft' as const,
    hiring_manager_name: f.hiring_manager_name.trim() || null,
    hiring_manager_email: f.hiring_manager_email.trim() || null,
    budget_currency: f.budget_currency.trim() || 'INR',
    budget_min: f.budget_min ? parseInt(f.budget_min, 10) : null,
    budget_max: f.budget_max ? parseInt(f.budget_max, 10) : null,
    location: f.location.trim() || null,
    employment_type: f.employment_type.trim() || null,
    business_justification: f.business_justification.trim() || null,
    internal_notes: mergedNotes,
    target_start_date: f.target_start_date || null,
    must_have_skills: f.must_have_skills,
    nice_to_have_skills: f.nice_to_have_skills,
  };
}

export function RequisitionNewWizardModal({
  open,
  onClose,
  onCreated,
  orgId,
  userId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (req: Requisition) => void;
  orgId: string;
  userId: string;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [form, setForm] = useState<WizardForm>(emptyForm());
  const [clients, setClients] = useState<Client[]>([]);
  const [managerSuggestions, setManagerSuggestions] = useState<ManagerSuggestion[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mustInput, setMustInput] = useState('');
  const [niceInput, setNiceInput] = useState('');

  /* Hiring manager autofill — parent-level state (avoids nested-component hook bug) */
  const [hmDropOpen, setHmDropOpen] = useState(false);
  const hmRef = useRef<HTMLDivElement>(null);

  /* AI side panel state */
  const [aiLoading, setAiLoading]   = useState(false);
  const [aiOutput, setAiOutput]     = useState('');  // only for custom prompts
  const [aiError, setAiError]       = useState('');
  const [aiCustom, setAiCustom]     = useState('');
  const [aiApplied, setAiApplied]   = useState('');  // brief "Applied ✓" feedback label
  const [templatesOpen, setTemplatesOpen] = useState(true);  // open by default
  /* Role title suggestions — from DB past titles + template titles */
  const [roleTitleSuggestions, setRoleTitleSuggestions] = useState<string[]>([]);
  const [roleTitleDropOpen, setRoleTitleDropOpen] = useState(false);
  const roleTitleRef = useRef<HTMLDivElement>(null);
  type TemplateReq = { id: string; title: string; department: string | null; client_name: string | null };
  const [templates, setTemplates] = useState<TemplateReq[]>([]);

  useEffect(() => {
    if (open) {
      setStep(1); setDraftId(null); setForm(emptyForm());
      setError(''); setMustInput(''); setNiceInput('');
      setManagerSuggestions([]);
    }
  }, [open]);

  useEffect(() => {
    if (!orgId) return;
    clientService.list(orgId).then((r) => setClients(Array.isArray(r) ? r : (r as { data: Client[] }).data));
  }, [orgId]);

  /* Build autofill suggestions: only client-side hiring managers from past requisitions.
     Org users are consultancy staff (coordinators/facilitators), NOT the client's hiring managers. */
  useEffect(() => {
    if (!orgId || !form.client_id) { setManagerSuggestions([]); return; }

    supabase
      .from('ggj_b2b_requisitions')
      .select('hiring_manager_name, hiring_manager_email')
      .eq('org_id', orgId)
      .eq('client_id', form.client_id)
      .not('hiring_manager_name', 'is', null)
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data }) => {
        const rows = (data ?? []) as { hiring_manager_name: string | null; hiring_manager_email: string | null }[];
        const seen = new Set<string>();
        const list: ManagerSuggestion[] = [];
        for (const r of rows) {
          if (!r.hiring_manager_name) continue;
          const key = (r.hiring_manager_email ?? r.hiring_manager_name).toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            list.push({
              key: `pr-${key}`,
              full_name: r.hiring_manager_name,
              email: r.hiring_manager_email ?? null,
              source: 'past_req',
            });
          }
        }
        setManagerSuggestions(list);
      })
      .catch(() => setManagerSuggestions([]));
  }, [orgId, form.client_id]);

  /* Fetch template requisitions from DB (for "copy from past") */
  useEffect(() => {
    if (!orgId) return;
    supabase
      .from('ggj_b2b_requisitions')
      .select('id, title, department, client_id')
      .eq('org_id', orgId)
      .in('status', ['filled', 'approved', 'sourcing'])
      .order('updated_at', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        if (!data) return;
        setTemplates(
          (data as { id: string; title: string; department: string | null; client_id: string }[]).map((r) => ({
            id: r.id,
            title: r.title,
            department: r.department ?? null,
            client_name: clients.find((c) => c.id === r.client_id)?.name ?? null,
          }))
        );
      })
      .catch(() => {});
  }, [orgId, clients]);

  /* Fetch role title suggestions from past requisitions */
  useEffect(() => {
    if (!orgId) return;
    supabase
      .from('ggj_b2b_requisitions')
      .select('title')
      .eq('org_id', orgId)
      .order('updated_at', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        const dbTitles = ((data ?? []) as { title: string }[]).map((r) => r.title).filter(Boolean);
        const tplTitles = REQUISITION_TEMPLATES.map((t) => t.patch.title ?? t.name);
        const all = [...new Set([...tplTitles, ...dbTitles])];
        setRoleTitleSuggestions(all);
      })
      .catch(() => {
        setRoleTitleSuggestions(REQUISITION_TEMPLATES.map((t) => t.patch.title ?? t.name));
      });
  }, [orgId]);

  /* Close role title dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (roleTitleRef.current && !roleTitleRef.current.contains(e.target as Node))
        setRoleTitleDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runWizardAi = async (userPrompt: string, applyTo?: 'must' | 'nice' | 'justification' | 'notes') => {
    setAiError(''); setAiLoading(true); setAiOutput(''); setAiApplied('');
    const contextLines = [
      `Role: ${form.title || '(not set)'}`,
      form.department ? `Department: ${form.department}` : '',
      selectedClient ? `Client: ${selectedClient.name}` : '',
      form.employment_type ? `Employment: ${form.employment_type}` : '',
      form.location ? `Location: ${form.location}` : '',
      form.business_justification ? `Justification: ${form.business_justification}` : '',
      form.must_have_skills.length ? `Must-have: ${form.must_have_skills.join(', ')}` : '',
      form.nice_to_have_skills.length ? `Nice-to-have: ${form.nice_to_have_skills.join(', ')}` : '',
    ].filter(Boolean).join('\n');
    try {
      const { content } = await invokeLlamaChat(
        [
          { role: 'system', content: AI_SYSTEM },
          { role: 'user', content: `${contextLines}\n\n---\n\nTask:\n${userPrompt}` },
        ],
        undefined,
        { orgId, feature: 'requisition_ai', orgUserId: userId }
      );
      if (applyTo) {
        /* Auto-apply directly to form field */
        if (applyTo === 'must') {
          const skills = parseSkillLines(content);
          if (skills.length) {
            setForm((f) => ({ ...f, must_have_skills: [...new Set([...f.must_have_skills, ...skills])] }));
            setAiApplied(`${skills.length} skills added to Must-have ✓`);
          }
        } else if (applyTo === 'nice') {
          const skills = parseSkillLines(content);
          if (skills.length) {
            setForm((f) => ({ ...f, nice_to_have_skills: [...new Set([...f.nice_to_have_skills, ...skills])] }));
            setAiApplied(`${skills.length} skills added to Nice-to-have ✓`);
          }
        } else if (applyTo === 'justification') {
          setForm((f) => ({ ...f, business_justification: stripMarkdown(content) }));
          setAiApplied('Justification updated ✓');
        } else if (applyTo === 'notes') {
          setForm((f) => ({ ...f, internal_notes: stripMarkdown(content) }));
          setAiApplied('Approval summary added ✓');
        }
        setTimeout(() => setAiApplied(''), 3000);
      } else {
        setAiOutput(content);
      }
    } catch (e: unknown) {
      setAiError(e instanceof Error ? e.message : 'AI request failed');
    } finally {
      setAiLoading(false);
    }
  };

  const copyFromTemplate = async (tplId: string) => {
    try {
      const tpl = await requisitionService.getById(tplId);
      if (!tpl) return;
      setForm((f) => ({
        ...f,
        title: tpl.title ?? f.title,
        department: tpl.department ?? f.department,
        employment_type: tpl.employment_type ?? f.employment_type,
        location: tpl.location ?? f.location,
        must_have_skills: tpl.must_have_skills?.length ? tpl.must_have_skills : f.must_have_skills,
        nice_to_have_skills: tpl.nice_to_have_skills?.length ? tpl.nice_to_have_skills : f.nice_to_have_skills,
        business_justification: tpl.business_justification ?? f.business_justification,
        budget_currency: tpl.budget_currency ?? f.budget_currency,
        budget_min: tpl.budget_min != null ? String(tpl.budget_min) : f.budget_min,
        budget_max: tpl.budget_max != null ? String(tpl.budget_max) : f.budget_max,
        headcount: tpl.headcount != null ? String(tpl.headcount) : f.headcount,
        priority: tpl.priority ?? f.priority,
      }));
      setTemplatesOpen(false);
    } catch { /* ignore */ }
  };

  const selectedClient = useMemo(() => clients.find((c) => c.id === form.client_id), [clients, form.client_id]);

  const set = (field: keyof WizardForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const persist = useCallback(async (nextStep: number | null, opts?: { finish?: boolean }): Promise<Requisition | null> => {
    if (!orgId) return null;
    setSaving(true);
    setError('');
    try {
      const fields = toBaseFields(form);
      const stepVal = opts?.finish ? null : nextStep;
      if (draftId) {
        const updated = await requisitionService.update(draftId, {
          ...fields, client_id: form.client_id, intake_wizard_step: stepVal,
        } as Partial<Requisition>);
        return updated;
      }
      const insert: RequisitionInsert = {
        org_id: orgId, client_id: form.client_id, created_by: userId, ...fields,
        submitted_at: null, approved_at: null, rejected_at: null,
        approver_user_id: null, approval_notes: null, created_job_id: null, job_id: null,
        intake_wizard_step: stepVal ?? 1,
      };
      const created = await requisitionService.create(insert);
      setDraftId(created.id);
      return created;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  }, [draftId, form, orgId, userId]);

  const validateStep1 = () => {
    if (!form.title.trim()) { setError('Role title is required.'); return false; }
    if (!form.client_id) { setError('Select a client.'); return false; }
    if (!isClientReadyForRecruitment(selectedClient)) {
      setError('This client is still in onboarding — complete compliance first.');
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    setError('');
    if (step === 1) {
      if (!validateStep1()) return;
      if (!draftId) { const r = await persist(2); if (r) setStep(2); return; }
      const r = await persist(Math.min(TOTAL, step + 1));
      if (r) setStep((s) => Math.min(TOTAL, s + 1));
      return;
    }
    if (step < TOTAL) { const r = await persist(step + 1); if (r) setStep((s) => s + 1); }
  };

  const handlePrev = () => {
    setError('');
    if (step <= 1) return;
    const prev = step - 1;
    setStep(prev);
    if (draftId) void persist(prev);
  };

  const handleFinish = async () => {
    if (!draftId) { setError('Use Next on step 1 to create the draft first.'); return; }
    const r = await persist(null, { finish: true });
    if (r) {
      onCreated(r);
      onClose();
      navigate(`/requisitions/${r.id}?focus=client`);
    }
  };

  const handleSaveDraft = async () => {
    if (!draftId) { if (!validateStep1()) return; }
    await persist(step);
  };

  const addMust = () => {
    const s = mustInput.trim();
    if (s && !form.must_have_skills.includes(s))
      setForm((f) => ({ ...f, must_have_skills: [...f.must_have_skills, s] }));
    setMustInput('');
  };

  const addNice = () => {
    const s = niceInput.trim();
    if (s && !form.nice_to_have_skills.includes(s))
      setForm((f) => ({ ...f, nice_to_have_skills: [...f.nice_to_have_skills, s] }));
    setNiceInput('');
  };

  const curStep = STEPS[step - 1];
  const CurIcon = curStep.icon;

  /* close hiring manager dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (hmRef.current && !hmRef.current.contains(e.target as Node)) setHmDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hmQuery = form.hiring_manager_name.toLowerCase() + form.hiring_manager_email.toLowerCase();
  const hmMatches = useMemo(() => {
    const q = form.hiring_manager_name.toLowerCase();
    const eq = form.hiring_manager_email.toLowerCase();
    return managerSuggestions.filter((s) =>
      (!q && !eq) ||
      s.full_name.toLowerCase().includes(q || eq) ||
      (s.email ?? '').toLowerCase().includes(eq || q),
    ).slice(0, 8);
  }, [managerSuggestions, hmQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Requisition"
      subtitle="Capture client hiring intent — steps guide you through intake."
      icon={<ClipboardList className="w-4 h-4" />}
      size="2xl"
    >
      {/* ── Step pill nav ── */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {STEPS.map((s) => {
          const done   = s.n < step;
          const active = s.n === step;
          const locked = !draftId && s.n > 1;
          return (
            <button
              key={s.n}
              type="button"
              disabled={locked}
              onClick={() => {
                if (locked) return;
                setStep(s.n);
                if (draftId) void persist(s.n);
              }}
              className={[
                'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all border',
                locked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
              ].join(' ')}
              style={
                active
                  ? { backgroundColor: BRAND, borderColor: BRAND, color: '#fff' }
                  : done
                    ? { backgroundColor: `${BRAND}18`, borderColor: `${BRAND}55`, color: BRAND }
                    : { backgroundColor: 'var(--vscode-bg-elevated)', borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-tertiary)' }
              }
            >
              {done && <Check className="w-3 h-3" />}
              {s.n}. {s.title}
            </button>
          );
        })}
      </div>

      {/* ── Step header ── */}
      <div className="flex items-center gap-3 mb-4 p-3 rounded-xl border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)]">
        <span className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
          style={{ backgroundColor: `${BRAND}20`, color: BRAND }}>
          <CurIcon className="w-4 h-4" />
        </span>
        <div>
          <p className="text-sm font-semibold vscode-text-primary">{curStep.title}</p>
          <p className="text-xs vscode-text-tertiary">{curStep.subtitle}</p>
        </div>
        <span className="ml-auto text-xs vscode-text-tertiary font-mono">{step}/{TOTAL}</span>
      </div>

      {/* ── Error ── */}
      {error && (
        <p className="text-xs text-red-500 mb-3 font-medium bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>
      )}

      {/* ── Two-column body: form left | AI hints right ── */}
      <div className="flex gap-4">

      {/* ── Form body ── */}
      <div className="flex-1 min-h-[300px] max-h-[440px] overflow-y-auto pr-1">

        {/* Step 1: Client & role */}
        {step === 1 && (
          <div className="space-y-5">

            {/* ── GROUP 1: Who is hiring ── */}
            <div className="rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
              style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
              <div className="px-3 py-2 border-b border-[var(--vscode-border-primary)]"
                style={{ backgroundColor: 'var(--vscode-bg-secondary)' }}>
                <p className="text-xs font-semibold vscode-text-secondary">Who is hiring</p>
              </div>
              <div className="p-3">
                <label className="label text-xs vscode-text-secondary">Client <span className="text-red-500">*</span></label>
                <select className="input w-full" value={form.client_id} onChange={set('client_id')}>
                  <option value="">Select client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            {/* ── GROUP 2: What role ── */}
            <div className="rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
              style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
              <div className="px-3 py-2 border-b border-[var(--vscode-border-primary)]"
                style={{ backgroundColor: 'var(--vscode-bg-secondary)' }}>
                <p className="text-xs font-semibold vscode-text-secondary">Role definition</p>
                <p className="text-[10px] vscode-text-tertiary mt-0.5">Start from a template or type a custom title</p>
              </div>
              <div className="p-3 space-y-3">
                {/* Role Templates */}
                <div>
                  <label className="label text-xs vscode-text-secondary">Role template</label>
                  <select
                    className="input w-full"
                    value={REQUISITION_TEMPLATES.find((t) => (t.patch.title ?? t.name) === form.title)?.id ?? ''}
                    onChange={(e) => {
                      const tpl = REQUISITION_TEMPLATES.find((t) => t.id === e.target.value);
                      if (!tpl) return;
                      setForm((f) => ({
                        ...f,
                        title:                  tpl.patch.title ?? f.title,
                        department:             tpl.patch.department ?? f.department,
                        employment_type:        tpl.patch.employment_type ?? f.employment_type,
                        location:               tpl.patch.location ?? f.location,
                        must_have_skills:       tpl.patch.must_have_skills?.length ? tpl.patch.must_have_skills : f.must_have_skills,
                        nice_to_have_skills:    tpl.patch.nice_to_have_skills?.length ? tpl.patch.nice_to_have_skills : f.nice_to_have_skills,
                        business_justification: tpl.patch.business_justification ?? f.business_justification,
                      }));
                      setAiApplied(`"${tpl.name}" applied ✓`);
                      setTimeout(() => setAiApplied(''), 2500);
                    }}>
                    <option value="">— select a preset to auto-fill fields…</option>
                    {REQUISITION_TEMPLATES.map((t) => (
                      <option key={t.id} value={t.id}>{t.category} · {t.name}</option>
                    ))}
                  </select>
                </div>

                {/* Role title autofill */}
                <div className="relative" ref={roleTitleRef}>
                  <label className="label text-xs vscode-text-secondary">Role title <span className="text-red-500">*</span></label>
                  <input
                    className="input w-full"
                    value={form.title}
                    autoComplete="off"
                    placeholder="e.g. Senior Backend Engineer"
                    onChange={(e) => { setForm((f) => ({ ...f, title: e.target.value })); setRoleTitleDropOpen(true); }}
                    onFocus={() => setRoleTitleDropOpen(true)}
                  />
                  {roleTitleDropOpen && (() => {
                    const q = form.title.toLowerCase();
                    const matches = roleTitleSuggestions.filter((t) => !q || t.toLowerCase().includes(q)).slice(0, 8);
                    return matches.length > 0 ? (
                      <ul className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-[var(--vscode-border-primary)] overflow-hidden shadow-xl"
                        style={{ background: 'var(--vscode-bg-secondary)' }}>
                        {matches.map((title) => {
                          const tpl = REQUISITION_TEMPLATES.find((t) => (t.patch.title ?? t.name) === title);
                          return (
                            <li key={title}>
                              <button type="button" onMouseDown={() => {
                                setForm((f) => ({
                                  ...f, title,
                                  department:             tpl?.patch.department ?? f.department,
                                  employment_type:        tpl?.patch.employment_type ?? f.employment_type,
                                  location:               tpl?.patch.location ?? f.location,
                                  must_have_skills:       tpl?.patch.must_have_skills?.length ? tpl.patch.must_have_skills : f.must_have_skills,
                                  nice_to_have_skills:    tpl?.patch.nice_to_have_skills?.length ? tpl.patch.nice_to_have_skills : f.nice_to_have_skills,
                                  business_justification: tpl?.patch.business_justification ?? f.business_justification,
                                }));
                                setRoleTitleDropOpen(false);
                              }} className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--vscode-bg-hover)] flex items-center gap-2">
                                <span className="font-medium vscode-text-primary truncate flex-1">{title}</span>
                                {tpl && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0"
                                    style={{ backgroundColor: `${BRAND}20`, color: BRAND }}>Template</span>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null;
                  })()}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="label text-xs vscode-text-secondary">Department</label>
                    <input className="input w-full" value={form.department} onChange={set('department')} placeholder="Engineering, Product…" />
                  </div>
                  <div>
                    <label className="label text-xs vscode-text-secondary">Headcount</label>
                    <input type="number" min={1} className="input w-full" value={form.headcount} onChange={set('headcount')} />
                  </div>
                </div>
              </div>
            </div>

            {/* ── GROUP 3: Intake metadata ── */}
            <div className="rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
              style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
              <div className="px-3 py-2 border-b border-[var(--vscode-border-primary)]"
                style={{ backgroundColor: 'var(--vscode-bg-secondary)' }}>
                <p className="text-xs font-semibold vscode-text-secondary">Intake metadata</p>
              </div>
              <div className="p-3">
                <label className="label text-xs vscode-text-secondary mb-2 block">Priority</label>
                <div className="flex flex-wrap gap-2">
                  {REQUISITION_PRIORITIES.map((p) => {
                    const cfg = PRIORITY_CONFIG[p];
                    const active = form.priority === p;
                    return (
                      <button key={p} type="button"
                        onClick={() => setForm((f) => ({ ...f, priority: p }))}
                        className="px-3 py-1 rounded-full text-xs font-semibold border transition-all"
                        style={{
                          color: active ? '#fff' : cfg.color,
                          backgroundColor: active ? cfg.color : cfg.bg,
                          borderColor: active ? cfg.color : cfg.border,
                          boxShadow: active ? `0 0 0 2px ${cfg.color}30` : undefined,
                        }}>
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: People & contacts */}
        {step === 2 && (
          <div className="space-y-5">

            {/* ── GROUP 1: Primary Hiring Manager ── */}
            <div className="rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
              style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
              <div className="px-3 py-2 border-b border-[var(--vscode-border-primary)] flex items-center gap-2"
                style={{ backgroundColor: `${BRAND}10` }}>
                <Users className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRAND }} />
                <p className="text-xs font-semibold" style={{ color: BRAND }}>Primary hiring manager</p>
                <span className="text-[10px] vscode-text-tertiary ml-auto">Decision-maker who owns this role</span>
              </div>
              <div className="p-3 space-y-3">
                {/* Autofill hint */}
                {form.client_id && managerSuggestions.length > 0 && (
                  <div className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs"
                    style={{ borderColor: `${BRAND}30`, backgroundColor: `${BRAND}08`, color: BRAND }}>
                    <Sparkles className="w-3 h-3 flex-shrink-0" />
                    {managerSuggestions.length} known manager{managerSuggestions.length > 1 ? 's' : ''} for {selectedClient?.name} — start typing to autofill both fields
                  </div>
                )}
                {form.client_id && managerSuggestions.length === 0 && (
                  <div className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs vscode-text-tertiary"
                    style={{ borderColor: 'var(--vscode-border-primary)', backgroundColor: 'var(--vscode-bg-secondary)' }}>
                    <Users className="w-3 h-3 flex-shrink-0" />
                    No past contacts for this client — enter the hiring manager details manually
                  </div>
                )}
                {/* Unified hiring-manager picker — state lives in parent to avoid hook re-init */}
                <div ref={hmRef} className="relative">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="label text-xs vscode-text-secondary">Full name *</label>
                      <input
                        className="input w-full"
                        value={form.hiring_manager_name}
                        placeholder="e.g. Priya Sharma"
                        autoComplete="off"
                        onChange={(e) => { setForm((f) => ({ ...f, hiring_manager_name: e.target.value })); setHmDropOpen(true); }}
                        onFocus={() => setHmDropOpen(true)}
                      />
                    </div>
                    <div>
                      <label className="label text-xs vscode-text-secondary">Work email *</label>
                      <input
                        type="email"
                        className="input w-full"
                        value={form.hiring_manager_email}
                        placeholder="priya@company.com"
                        autoComplete="off"
                        onChange={(e) => { setForm((f) => ({ ...f, hiring_manager_email: e.target.value })); setHmDropOpen(true); }}
                        onFocus={() => setHmDropOpen(true)}
                      />
                    </div>
                  </div>
                  {hmDropOpen && hmMatches.length > 0 && (
                    <ul className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-[var(--vscode-border-primary)] overflow-hidden shadow-lg"
                      style={{ background: 'var(--vscode-bg-secondary)' }}>
                      {hmMatches.map((s) => (
                        <li key={s.key}>
                          <button type="button"
                            onMouseDown={() => { setForm((f) => ({ ...f, hiring_manager_name: s.full_name, hiring_manager_email: s.email ?? f.hiring_manager_email })); setHmDropOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--vscode-bg-hover)] flex items-center gap-2">
                            <span className="font-medium vscode-text-primary truncate flex-1">{s.full_name}</span>
                            {s.email && <span className="text-xs vscode-text-tertiary truncate shrink-0 max-w-[160px]">{s.email}</span>}
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0"
                              style={{ backgroundColor: `${BRAND}20`, color: BRAND }}>past</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {/* Preview card when both fields filled */}
                {form.hiring_manager_name && form.hiring_manager_email && (
                  <div className="flex items-center gap-2 rounded-lg border px-3 py-2"
                    style={{ borderColor: '#10b98140', backgroundColor: '#10b98108' }}>
                    <span className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: '#10b981' }}>
                      {form.hiring_manager_name.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold vscode-text-primary truncate">{form.hiring_manager_name}</p>
                      <p className="text-[10px] text-emerald-400 truncate">{form.hiring_manager_email}</p>
                    </div>
                    <Check className="w-3.5 h-3.5 flex-shrink-0 text-emerald-400 ml-auto" />
                  </div>
                )}
              </div>
            </div>

            {/* ── GROUP 2: Alternate / Backup Contact ── */}
            <div className="rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
              style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
              <div className="px-3 py-2 border-b border-[var(--vscode-border-primary)] flex items-center gap-2"
                style={{ backgroundColor: 'var(--vscode-bg-secondary)' }}>
                <Users className="w-3.5 h-3.5 flex-shrink-0 vscode-text-tertiary" />
                <p className="text-xs font-semibold vscode-text-secondary">Alternate SPOC</p>
                <span className="text-[10px] vscode-text-tertiary ml-1">optional</span>
              </div>
              <div className="p-3 space-y-3">
                <p className="text-xs vscode-text-tertiary leading-relaxed">
                  If the primary hiring manager is unavailable, the recruiter will follow up with this person for confirmations, feedback, and scheduling.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="label text-xs vscode-text-secondary">Full name</label>
                    <input className="input w-full" value={form.alt_spoc_name} onChange={set('alt_spoc_name')} placeholder="e.g. Rajan Mehta" />
                  </div>
                  <div>
                    <label className="label text-xs vscode-text-secondary">Work email</label>
                    <input type="email" className="input w-full" value={form.alt_spoc_email} onChange={set('alt_spoc_email')} placeholder="rajan@company.com" />
                  </div>
                </div>
              </div>
            </div>

            {/* ── GROUP 3: Work arrangement ── */}
            <div className="rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
              style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
              <div className="px-3 py-2 border-b border-[var(--vscode-border-primary)]"
                style={{ backgroundColor: 'var(--vscode-bg-secondary)' }}>
                <p className="text-xs font-semibold vscode-text-secondary">Work arrangement</p>
              </div>
              <div className="p-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label text-xs vscode-text-secondary">Location</label>
                  <input className="input w-full" value={form.location} onChange={set('location')} placeholder="City, Remote, Hybrid…" />
                </div>
                <div>
                  <label className="label text-xs vscode-text-secondary">Employment type</label>
                  <select className="input w-full" value={form.employment_type} onChange={set('employment_type')}>
                    <option value="">Select type…</option>
                    {['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship', 'Temporary'].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label text-xs vscode-text-secondary">Target start date</label>
                  <input type="date" className="input w-full" value={form.target_start_date} onChange={set('target_start_date')} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Role scope */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label text-xs vscode-text-secondary mb-0">
                  Business justification
                  <span className="ml-1 font-normal vscode-text-tertiary normal-case tracking-normal">— why this role is needed</span>
                </label>
                <button type="button"
                  className="flex items-center gap-1 text-xs font-medium rounded px-2 py-0.5 transition-colors hover:opacity-100 opacity-70"
                  style={{ color: BRAND, backgroundColor: `${BRAND}15` }}
                  onClick={() => {
                    if (!form.title) return;
                    const draft = `We are seeking a ${form.title}${form.department ? ` for the ${form.department} team` : ''}${form.location ? ` based in ${form.location}` : ''}. This role is critical to support ${selectedClient?.name ?? 'the client'}'s growth objectives. The hire will be responsible for key deliverables including [describe scope]. Headcount: ${form.headcount}.`;
                    setForm((f) => ({ ...f, business_justification: stripMarkdown(draft) }));
                  }}>
                  <Sparkles className="w-3 h-3" /> AI draft
                </button>
              </div>
              <textarea className="input w-full min-h-[100px] resize-y" value={form.business_justification} onChange={set('business_justification')} placeholder="Why is this role needed? Scope of work, key responsibilities, impact on the business…" />
            </div>
            <div>
              <label className="label text-xs vscode-text-secondary">Must-have skills</label>
              <div className="flex gap-2 mb-2">
                <input className="input flex-1" value={mustInput} onChange={(e) => setMustInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMust())} placeholder="Add skill, press Enter" />
                <button type="button" className="btn-secondary" onClick={addMust}>Add</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.must_have_skills.map((s) => (
                  <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, must_have_skills: f.must_have_skills.filter((x) => x !== s) }))}
                    className="rounded-md px-2 py-0.5 text-xs font-medium vscode-text-primary"
                    style={{ backgroundColor: `${BRAND}20`, border: `1px solid ${BRAND}40` }}>
                    {s} ×
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label text-xs vscode-text-secondary">Nice-to-have skills</label>
              <div className="flex gap-2 mb-2">
                <input className="input flex-1" value={niceInput} onChange={(e) => setNiceInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addNice())} placeholder="Add skill, press Enter" />
                <button type="button" className="btn-secondary" onClick={addNice}>Add</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.nice_to_have_skills.map((s) => (
                  <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, nice_to_have_skills: f.nice_to_have_skills.filter((x) => x !== s) }))}
                    className="rounded-md px-2 py-0.5 text-xs vscode-text-secondary"
                    style={{ backgroundColor: 'var(--vscode-bg-hover)', border: '1px solid var(--vscode-border-primary)' }}>
                    {s} ×
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label text-xs vscode-text-secondary mb-0">Internal notes</label>
                <div className="flex items-center gap-2">
                  {form.internal_notes && (
                    <button type="button"
                      className="text-xs px-2 py-0.5 rounded border vscode-text-tertiary hover:vscode-text-primary transition-colors"
                      style={{ borderColor: 'var(--vscode-border-primary)', backgroundColor: 'var(--vscode-bg-hover)' }}
                      onClick={() => setForm((f) => ({ ...f, internal_notes: '' }))}>
                      Clear ×
                    </button>
                  )}
                  <span className="flex items-center gap-1 text-xs opacity-50" style={{ color: BRAND }}>
                    <Sparkles className="w-3 h-3" /> AI on detail page
                  </span>
                </div>
              </div>
              <textarea className="input w-full min-h-[72px] resize-y" value={form.internal_notes} onChange={set('internal_notes')} placeholder="Notes visible only to the internal team…" />
            </div>
          </div>
        )}

        {/* Step 4: Compensation */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
              style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
              <div className="px-3 py-2 border-b border-[var(--vscode-border-primary)]"
                style={{ backgroundColor: 'var(--vscode-bg-secondary)' }}>
                <p className="text-xs font-semibold vscode-text-secondary">Salary / budget range</p>
                <p className="text-[10px] vscode-text-tertiary mt-0.5">Internal only — not published to candidates or career sites</p>
              </div>
              <div className="p-3 grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="label text-xs vscode-text-secondary">Currency</label>
                  <input className="input w-full" value={form.budget_currency} onChange={set('budget_currency')} placeholder="INR, USD, GBP…" />
                </div>
                <div>
                  <label className="label text-xs vscode-text-secondary">Minimum</label>
                  <input type="number" className="input w-full" value={form.budget_min} onChange={set('budget_min')} placeholder="e.g. 1200000" />
                </div>
                <div>
                  <label className="label text-xs vscode-text-secondary">Maximum</label>
                  <input type="number" className="input w-full" value={form.budget_max} onChange={set('budget_max')} placeholder="e.g. 1800000" />
                </div>
              </div>
              {/* Live preview */}
              {(form.budget_min || form.budget_max) && (
                <div className="px-3 pb-3">
                  <p className="text-xs vscode-text-tertiary mb-1">Range preview</p>
                  <p className="text-sm font-semibold vscode-text-primary">
                    {form.budget_currency} {form.budget_min ? Number(form.budget_min).toLocaleString() : '?'}
                    {' '}–{' '}
                    {form.budget_max ? Number(form.budget_max).toLocaleString() : '?'} per annum
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Review — formatted requisition document */}
        {step === 5 && (
          <div className="space-y-0 rounded-xl border border-[var(--vscode-border-primary)] overflow-hidden"
            style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>

            {/* Document header */}
            <div className="px-4 py-3 border-b border-[var(--vscode-border-primary)]"
              style={{ backgroundColor: `${BRAND}12` }}>
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-0.5"
                style={{ color: BRAND }}>Hiring Requisition — Draft</p>
              <p className="text-base font-bold vscode-text-primary">{form.title || '(Role title not set)'}</p>
              <p className="text-xs vscode-text-tertiary mt-0.5">
                {selectedClient?.name ?? '(No client)'}{form.department ? ` · ${form.department}` : ''}
              </p>
            </div>

            {/* Section: Role details */}
            <div className="px-4 py-3 border-b border-[var(--vscode-border-primary)]">
              <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-2">Role details</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                <span className="vscode-text-tertiary">Headcount</span>
                <span className="vscode-text-primary font-medium">{form.headcount}</span>
                <span className="vscode-text-tertiary">Priority</span>
                <span className="vscode-text-primary capitalize">{form.priority}</span>
                <span className="vscode-text-tertiary">Employment type</span>
                <span className="vscode-text-primary">{form.employment_type || '—'}</span>
                <span className="vscode-text-tertiary">Location</span>
                <span className="vscode-text-primary">{form.location || '—'}</span>
                <span className="vscode-text-tertiary">Target start</span>
                <span className="vscode-text-primary">{form.target_start_date || '—'}</span>
              </div>
            </div>

            {/* Section: Hiring contact */}
            <div className="px-4 py-3 border-b border-[var(--vscode-border-primary)]">
              <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-2">Hiring contact</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                <span className="vscode-text-tertiary">Hiring manager</span>
                <span className="vscode-text-primary">{form.hiring_manager_name || '—'}</span>
                <span className="vscode-text-tertiary">Manager email</span>
                <span className="vscode-text-primary">{form.hiring_manager_email || '—'}</span>
                {form.alt_spoc_name && <>
                  <span className="vscode-text-tertiary">Alt. SPOC</span>
                  <span className="vscode-text-primary">{form.alt_spoc_name}</span>
                  <span className="vscode-text-tertiary">Alt. SPOC email</span>
                  <span className="vscode-text-primary">{form.alt_spoc_email || '—'}</span>
                </>}
              </div>
            </div>

            {/* Section: Business justification */}
            {form.business_justification && (
              <div className="px-4 py-3 border-b border-[var(--vscode-border-primary)]">
                <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-2">Business justification</p>
                <p className="text-xs vscode-text-secondary leading-relaxed whitespace-pre-wrap">{form.business_justification}</p>
              </div>
            )}

            {/* Section: Skills */}
            {(form.must_have_skills.length > 0 || form.nice_to_have_skills.length > 0) && (
              <div className="px-4 py-3 border-b border-[var(--vscode-border-primary)]">
                <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-2">Required skills</p>
                {form.must_have_skills.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[10px] vscode-text-tertiary mb-1.5">Must-have</p>
                    <div className="flex flex-wrap gap-1">
                      {form.must_have_skills.map((s) => (
                        <span key={s} className="rounded px-2 py-0.5 text-xs font-medium"
                          style={{ backgroundColor: `${BRAND}20`, border: `1px solid ${BRAND}40`, color: BRAND }}>{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {form.nice_to_have_skills.length > 0 && (
                  <div>
                    <p className="text-[10px] vscode-text-tertiary mb-1.5">Nice-to-have</p>
                    <div className="flex flex-wrap gap-1">
                      {form.nice_to_have_skills.map((s) => (
                        <span key={s} className="rounded px-2 py-0.5 text-xs vscode-text-secondary"
                          style={{ backgroundColor: 'var(--vscode-bg-hover)', border: '1px solid var(--vscode-border-primary)' }}>{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Section: Compensation */}
            <div className="px-4 py-3 border-b border-[var(--vscode-border-primary)]">
              <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-2">Compensation</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                <span className="vscode-text-tertiary">Budget range</span>
                <span className="vscode-text-primary font-medium">
                  {form.budget_min || form.budget_max
                    ? `${form.budget_currency} ${form.budget_min || '?'} – ${form.budget_max || '?'}`
                    : '—'}
                </span>
                <span className="vscode-text-tertiary">Currency</span>
                <span className="vscode-text-primary">{form.budget_currency}</span>
              </div>
            </div>

            {/* Section: Internal notes */}
            {(form.internal_notes || form.alt_spoc_name) && (
              <div className="px-4 py-3 border-b border-[var(--vscode-border-primary)]">
                <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-2">Internal notes</p>
                {form.alt_spoc_name && (
                  <p className="text-xs vscode-text-secondary mb-1">
                    Alt. SPOC: <strong>{form.alt_spoc_name}</strong>{form.alt_spoc_email ? ` &lt;${form.alt_spoc_email}&gt;` : ''}
                  </p>
                )}
                {form.internal_notes && (
                  <p className="text-xs vscode-text-secondary leading-relaxed whitespace-pre-wrap">{form.internal_notes}</p>
                )}
              </div>
            )}

            {/* Footer note */}
            <div className="px-4 py-2.5">
              <p className="text-[10px] vscode-text-tertiary">
                Saved as <strong>Draft</strong> — complete the client gate and approval flow from the requisition detail page.
              </p>
            </div>
          </div>
        )}
      </div>{/* end form body */}

      {/* ── Right: AI hints + templates panel ── */}
      <div className="w-[220px] flex-shrink-0 border-l border-[var(--vscode-border-primary)] pl-4 max-h-[440px] overflow-y-auto space-y-4">

        {/* Applied feedback banner */}
        {aiApplied && (
          <div className="rounded-lg px-3 py-2 text-xs font-semibold text-center"
            style={{ backgroundColor: `${BRAND}20`, color: BRAND, border: `1px solid ${BRAND}40` }}>
            {aiApplied}
          </div>
        )}

        {/* Past requisitions — copy from a previous intake */}
        {templates.length > 0 && (
          <div>
            <button type="button"
              className="w-full flex items-center justify-between text-xs font-semibold vscode-text-secondary uppercase tracking-wider py-1 hover:opacity-80"
              onClick={() => setTemplatesOpen((o) => !o)}>
              <span className="flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Past requisitions
              </span>
              {templatesOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {templatesOpen && (
              <div className="mt-2 space-y-1.5">
                {templates.map((tpl) => (
                  <button key={tpl.id} type="button"
                    onClick={() => void copyFromTemplate(tpl.id)}
                    className="w-full text-left rounded-lg border border-[var(--vscode-border-primary)] px-2.5 py-2 hover:bg-[var(--vscode-bg-hover)] transition-colors group">
                    <div className="flex items-start gap-1.5">
                      <Copy className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-50 group-hover:opacity-100" style={{ color: BRAND }} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium vscode-text-primary leading-tight truncate">{tpl.title}</p>
                        {tpl.client_name && <p className="text-[10px] vscode-text-tertiary truncate">{tpl.client_name}</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step tips */}
        {step === 1 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">Tips</p>
            {[
              'Use standard job title formats (e.g. "Senior React Engineer")',
              'Select the correct client — this drives the hiring-client email flow',
              'Set headcount accurately — each seat needs a separate sourcing track',
            ].map((tip) => (
              <p key={tip} className="text-xs vscode-text-tertiary leading-relaxed border-l-2 pl-2"
                style={{ borderColor: `${BRAND}60` }}>{tip}</p>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">Tips</p>
            {[
              'Hiring manager email is used to send the client gate outreach email',
              'Remote roles attract more candidates — be explicit about arrangement',
              '"Contract" vs "Full-time" changes the entire sourcing and legal pathway',
            ].map((tip) => (
              <p key={tip} className="text-xs vscode-text-tertiary leading-relaxed border-l-2 pl-2"
                style={{ borderColor: `${BRAND}60` }}>{tip}</p>
            ))}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">Budget benchmarks</p>
            {[
              ['Frontend / React', '18–35 LPA'],
              ['Backend / Node', '20–40 LPA'],
              ['Data Engineer', '22–45 LPA'],
              ['Product Manager', '25–55 LPA'],
              ['UX Designer', '12–28 LPA'],
              ['DevOps / SRE', '20–50 LPA'],
            ].map(([role, range]) => (
              <div key={role} className="flex justify-between text-xs">
                <span className="vscode-text-tertiary truncate">{role}</span>
                <span className="font-mono vscode-text-secondary ml-2 flex-shrink-0">{range}</span>
              </div>
            ))}
            <p className="text-[10px] vscode-text-tertiary mt-1 italic">INR indicative ranges for India market.</p>
          </div>
        )}

        {/* AI prompts for steps 3 and 5 */}
        {(step === 3 || step === 5) && STEP_PROMPTS[step] && (
          <div className="space-y-3">
            <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND }} /> AI prompts
            </p>
            <p className="text-[10px] vscode-text-tertiary leading-relaxed">
              Results apply directly to the form on the left.
            </p>
            <div className="space-y-1.5">
              {STEP_PROMPTS[step].map((p) => (
                <button key={p.label} type="button"
                  disabled={aiLoading}
                  className="w-full text-left text-xs rounded-lg px-2.5 py-2 font-medium transition-colors border hover:opacity-90"
                  style={{ borderColor: `${BRAND}40`, backgroundColor: `${BRAND}10`, color: BRAND }}
                  onClick={() => void runWizardAi(p.prompt, p.autoApply ? p.apply : undefined)}>
                  {aiLoading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                  {p.label}
                </button>
              ))}
            </div>

            {/* Custom prompt — output shows inline since destination is unknown */}
            <div className="space-y-1.5 pt-2 border-t border-[var(--vscode-border-primary)]">
              <p className="text-[10px] font-semibold vscode-text-tertiary uppercase tracking-wider">Custom ask</p>
              <textarea
                className="input w-full text-xs resize-none"
                rows={2}
                placeholder="Ask AI anything…"
                value={aiCustom}
                onChange={(e) => setAiCustom(e.target.value)}
                disabled={aiLoading}
              />
              <button type="button"
                disabled={aiLoading || !aiCustom.trim()}
                className="w-full btn-secondary text-xs flex items-center justify-center gap-1"
                onClick={() => void runWizardAi(aiCustom.trim())}>
                {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                Run
              </button>
            </div>

            {aiError && <p className="text-xs text-red-400">{aiError}</p>}

            {/* Output only for custom (non-auto-apply) prompts */}
            {aiOutput && !aiLoading && (
              <div className="rounded-lg border border-[var(--vscode-border-primary)] p-2.5 space-y-2"
                style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
                <p className="text-[10px] font-semibold vscode-text-tertiary uppercase tracking-wider">Output</p>
                <p className="text-xs vscode-text-secondary whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">{aiOutput}</p>
              </div>
            )}
          </div>
        )}

        {/* Checklist for review step */}
        {step === 5 && (
          <div className="space-y-2 pt-2 border-t border-[var(--vscode-border-primary)]">
            <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">Checklist</p>
            {[
              { label: 'Client selected', ok: !!form.client_id },
              { label: 'Role title', ok: !!form.title.trim() },
              { label: 'Hiring manager', ok: !!form.hiring_manager_name.trim() },
              { label: 'HM email', ok: !!form.hiring_manager_email.trim() },
              { label: 'Skills added', ok: form.must_have_skills.length > 0 },
              { label: 'Justification', ok: !!form.business_justification.trim() },
              { label: 'Budget set', ok: !!(form.budget_min || form.budget_max) },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 text-xs">
                <span className={`w-3.5 h-3.5 rounded-full flex-shrink-0 flex items-center justify-center ${item.ok ? 'bg-emerald-500' : 'border border-[var(--vscode-border-primary)]'}`}>
                  {item.ok && <Check className="w-2 h-2 text-white" />}
                </span>
                <span className={item.ok ? 'vscode-text-secondary' : 'vscode-text-tertiary'}>{item.label}</span>
              </div>
            ))}
          </div>
        )}

      </div>{/* end right panel */}

      </div>{/* end two-column flex */}

      {/* ── Footer nav ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-[var(--vscode-border-primary)]">
        <button type="button" className="btn-secondary text-sm inline-flex items-center gap-2"
          onClick={handlePrev} disabled={saving || step <= 1}>
          <ChevronLeft className="h-4 w-4" /> [Previous]
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-secondary text-sm inline-flex items-center gap-2"
            onClick={handleSaveDraft} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            [Save draft]
          </button>
          {step < TOTAL ? (
            <button type="button" className="btn-primary text-sm inline-flex items-center gap-2"
              onClick={handleNext} disabled={saving}>
              [Next] <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" className="btn-primary text-sm inline-flex items-center gap-2"
              onClick={handleFinish} disabled={saving || !draftId}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              [Finish intake]
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
